<template>
  <div
    ref="rootEl"
    class="relative"
  >
    <TInputGroup>
      <template #prepend>
        <button
          type="button"
          class="flex items-center gap-1 cursor-pointer"
          :disabled="countryButtonDisabled"
          @click="open = !open"
        >
          <span aria-hidden="true">{{ selectedCountry?.flag }}</span>
          +{{ selectedCountry?.dialCode }}
          <TIcon
            icon="angles-up-down"
            size="sm"
            class="text-text-muted"
          />
        </button>
      </template>

      <TInput
        ref="field"
        v-bind="$attrs"
        :model-value="formatted"
        type="tel"
        inputmode="tel"
        autocomplete="tel-national"
        :disabled="disabled"
        :readonly="readonly"
        @update:model-value="onType"
        @focus="focused = true"
        @blur="focused = false"
      />
    </TInputGroup>

    <div
      v-if="open"
      class="absolute left-0 top-full z-10 w-full overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black/5"
    >
      <input
        v-model="query"
        type="text"
        class="w-full border-b border-border-default px-3 py-2 text-sm outline-none"
        :placeholder="t('label')"
        @keydown.stop
      >

      <ul
        class="max-h-60 overflow-auto text-sm"
        role="listbox"
      >
        <li
          v-for="countryOption in filteredCountries"
          :key="countryOption.iso2"
          class="flex cursor-default items-center gap-3 px-3 py-2 select-none hover:bg-primary-50"
          :class="{ 'bg-primary-100': countryOption.iso2 === country }"
          role="option"
          @mousedown.prevent
          @click="selectCountry(countryOption.iso2)"
        >
          <span aria-hidden="true">{{ countryOption.flag }}</span>
          <span class="min-w-0 flex-1 truncate">{{ countryOption.name }}</span>
          <span class="text-text-muted">+{{ countryOption.dialCode }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { INJECTION_KEYS } from '~/constants/injections'
import {
  type CountryCode,
  AsYouType,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from 'libphonenumber-js'

import type { Nullable } from '~/types'

const props = defineProps<{
  countryLocked?: boolean
  disabled?: boolean
  readonly?: boolean
}>()

const model = defineModel<string>({ default: '' })
const countryCallingCode = defineModel<string>('countryCallingCode', { default: '' })

defineOptions({ inheritAttrs: false })

const { t, locale } = useI18n()
const { geolocation } = useGeolocation()
const runtimeConfig = useRuntimeConfig()

const rootEl = useTemplateRef<HTMLElement>('rootEl')
const field = useTemplateRef<{ inputEl: Nullable<HTMLInputElement> }>('field')

const open = ref(false)
const query = ref('')
const focused = ref(false)
const formatted = ref('')
const country = ref<CountryCode>('CA')

const inputDisabled = inject(INJECTION_KEYS.inputGroupDisabled, ref(false))
const inputReadonly = inject(INJECTION_KEYS.inputGroupReadonly, ref(false))

const isDisabled = computed(() => props.disabled || inputDisabled.value)
const isReadonly = computed(() => props.readonly || inputReadonly.value)
const countryButtonDisabled = computed(() => isDisabled.value || isReadonly.value || props.countryLocked)

const preferredCountries = computed(() => {
  return runtimeConfig.public.supportedCountries.map((code: string) => code.toUpperCase())
})

const countryNames = computed(() => new Intl.DisplayNames([locale.value], { type: 'region' }))

const countries = computed(() => {
  const all = getCountries().map((iso2) => ({
    iso2,
    name: countryNames.value.of(iso2) ?? iso2,
    dialCode: getCountryCallingCode(iso2),
    flag: flagEmoji(iso2),
  }))

  const preferred = preferredCountries.value.flatMap((iso2) => {
    const match = all.find((countryOption) => countryOption.iso2 === iso2)
    return match ? [match] : []
  })

  const rest = all
    .filter((countryOption) => !preferredCountries.value.includes(countryOption.iso2))
    .sort((a, b) => a.name.localeCompare(b.name, locale.value))

  return [...preferred, ...rest]
})

// Filters the list of countries based on what the user inputs into the search
// input.
const filteredCountries = computed(() => {
  const q = query.value.trim().toLowerCase().replace('+', '')
  if (!q) return countries.value

  return countries.value.filter((countryOption) => {
    return countryOption.name.toLowerCase().includes(q)
      || countryOption.iso2.toLowerCase().includes(q)
      || countryOption.dialCode.includes(q)
  })
})

const selectedCountry = computed(() => {
  return countries.value.find((countryOption) => countryOption.iso2 === country.value) ?? countries.value[0]
})

// Reset the search field on visibility toggle.
watch(open, (isOpen) => {
  if (!isOpen) {
    query.value = ''
  }
})

// Typing writes both models. Re-parsing mid-keystroke moves the caret.
watch([model, countryCallingCode], () => {
  if (focused.value) {
    return
  }

  hydrate()
}, { immediate: true })

// Sets the default country code based on geolocation.
watch(() => geolocation.value?.countryCode, () => {
  if (focused.value || model.value || countryCallingCode.value) {
    return
  }

  applyCountry(defaultCountry())
})

onClickOutside(rootEl, () => {
  open.value = false
})

/**
 * Handles typing into the phone input field. Parses the raw value and reformats
 * it so it displays nicely.
 *
 * @param value The phone number input value
 */
function onType(value?: Nullable<number | string>) {
  const raw = String(value ?? '')
  const input = field.value?.inputEl
  const cursor = input?.selectionStart ?? raw.length
  const digitsBefore = raw.slice(0, cursor).replace(/\D/g, '').length

  const formatter = new AsYouType(country.value)
  const nextFormatted = formatter.input(raw)

  formatted.value = nextFormatted
  model.value = formatter.getNumber()?.nationalNumber ?? raw.replace(/\D/g, '')
  countryCallingCode.value = `+${getCountryCallingCode(country.value)}`

  // Puts the caret back into the same position where the user was typing.
  nextTick(() => {
    const pos = caretAfterDigits(nextFormatted, digitsBefore)
    input?.setSelectionRange(pos, pos)
  })
}

/**
 * Selects the country and reformats the input.
 *
 * @param iso2 The country code of the slected country
 */
function selectCountry(iso2: CountryCode) {
  applyCountry(iso2)
  open.value = false
  formatted.value = new AsYouType(iso2).input(model.value)
  field.value?.inputEl?.focus()
}

/**
 * Takes the raw stored values and turns them into preformatted ones.
 */
function hydrate() {
  const digits = model.value.replace(/\D/g, '')

  if (!digits) {
    formatted.value = ''
    applyCountry(countryFromCallingCode() ?? defaultCountry())
    return
  }

  const callingCode = (countryCallingCode.value || getCountryCallingCode(country.value)).replace('+', '')
  const parsed = parsePhoneNumberFromString(`+${callingCode}${digits}`)

  if (parsed?.country) {
    country.value = parsed.country
  }

  if (!countryCallingCode.value) {
    countryCallingCode.value = `+${getCountryCallingCode(country.value)}`
  }

  formatted.value = parsed?.formatNational() ?? digits
}

/**
 * Applies the selected country.
 *
 * @param iso2 The selected country's code
 */
function applyCountry(iso2: CountryCode) {
  country.value = iso2
  countryCallingCode.value = `+${getCountryCallingCode(iso2)}`
}

/**
 * Takes the country code from the geo location, or otherwise returns the
 * default (CA).
 */
function defaultCountry(): CountryCode {
  const code = geolocation.value?.countryCode?.toUpperCase()

  if (code && isCountryCode(code)) {
    return code
  }

  return 'CA'
}

/**
 * Turns a calling code (like +1), into an ISO country.
 * Takes the user's country into account (so Canada "wins" on +1 if that's the
 * user's location).
 */
function countryFromCallingCode(): CountryCode | undefined {
  const callingCode = countryCallingCode.value.replace('+', '')

  if (!callingCode) {
    return undefined
  }

  const fromGeo = defaultCountry()

  if (getCountryCallingCode(fromGeo) === callingCode) {
    return fromGeo
  }

  return countries.value.find((countryOption) => countryOption.dialCode === callingCode)?.iso2
}

/**
 * Checks if a given country is in the list.
 *
 * @param value The country code to lookup
 */
function isCountryCode(value: string): value is CountryCode {
  return getCountries().includes(value as CountryCode)
}

/**
 * This is quite cute: it returns the flag emoji for a given country code.
 * Support is really good across platforms, but falls back to initials (like
 * `CA`) if the OS doesn't have an emoji for it.
 *
 * @param iso2 The country code.
 */
function flagEmoji(iso2: string) {
  return iso2.toUpperCase().replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
}

/**
 * Returns the actual position of the caret based on the formatted number.
 *
 * @param value The formatted phone number string
 * @param digitCount Which digit in the unformatted number to check for
 *
 * @example caretAfterDigits('(613) 555-1234', 4) => 8
 */
function caretAfterDigits(value: string, digitCount: number) {
  if (digitCount <= 0) {
    return 0
  }

  let seen = 0

  for (let index = 0; index < value.length; index++) {
    if (/\d/.test(value[index] ?? '')) {
      seen++
    }

    if (seen === digitCount) {
      return index + 1
    }
  }

  return value.length
}
</script>

<i18n lang="json">
  {
    "en": {
      "label": "Select your country"
    },
    "fr": {
      "label": "Sélectionnez votre pays"
    }
  }
</i18n>
